import React, { createContext, useContext, useState, useEffect } from 'react';
import { inferSiteLanguageCode, SITE_LANGUAGE_CODES } from '../seo/localizedSiteRoutes';

export const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const inferLanguageFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const fromUrl = inferSiteLanguageCode(window.location.pathname);
  return fromUrl && SITE_LANGUAGE_CODES.includes(fromUrl) ? fromUrl : null;
};

const readInitialLanguage = () => {
  // 1) URL takes precedence — if a visitor lands on /es/contact, they
  //    must see Spanish before any other signal kicks in. This also
  //    means Google's localized index links work directly.
  if (typeof window !== 'undefined') {
    const fromUrl = inferLanguageFromUrl();
    if (fromUrl && fromUrl !== 'en') return fromUrl;
    if (fromUrl === 'en') return 'en';

    // 2) Fall back to the user's previously stored choice (when no
    //    explicit language is in the URL the visitor sees their last
    //    pick on subsequent visits).
    try {
      const stored = window.localStorage.getItem('emblem-language');
      if (stored && SITE_LANGUAGE_CODES.includes(stored)) return stored;
    } catch (_) {
      // Ignore storage failures.
    }
  }

  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(readInitialLanguage);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('emblem-language', language);
    } catch (_) {
      // Ignore storage failures (quota, private mode, blocked storage).
    }
  }, [language]);

  // Keep the context in sync with the URL on every navigation. Without
  // this, clicking from `/emblem-world` to `/fr/emblem-world` (via an
  // internal `<Link>`, browser back/forward, or pasted URL) leaves the
  // context stuck at the original language even though the URL has
  // changed — so the page renders in English on a French URL. We can't
  // use `useLocation` here because LanguageProvider wraps the Router;
  // listening to popstate + monkeypatching pushState/replaceState
  // covers every navigation path React Router uses internally.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncFromUrl = () => {
      const fromUrl = inferLanguageFromUrl();
      if (fromUrl && fromUrl !== language) {
        setLanguage(fromUrl);
      }
    };

    syncFromUrl();

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function patchedPushState(...args) {
      const result = originalPushState.apply(this, args);
      syncFromUrl();
      return result;
    };
    window.history.replaceState = function patchedReplaceState(...args) {
      const result = originalReplaceState.apply(this, args);
      syncFromUrl();
      return result;
    };

    window.addEventListener('popstate', syncFromUrl);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', syncFromUrl);
    };
  }, [language]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
  };

  const value = {
    language,
    setLanguage,
    changeLanguage,
    isEnglish: language === 'en',
    isFrench: language === 'fr',
    isGerman: language === 'de',
    isSpanish: language === 'es',
    isItalian: language === 'it',
    isPortuguese: language === 'pt',
    isChinese: language === 'zh',
    isArabic: language === 'ar'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
